/**
 * Eksportene under «artist» i katalogen (docs/EKSPORT.md §9 «Artist og ekstern med innlogging»).
 * API-halvdelen: `apps/api/src/export/definitions/artist/`.
 *
 * Alle fire handler om MENNESKET, ikke om organisasjonen som er aktiv: konsertene personen står på
 * (`app.my_concerts`, sak 216) og det som er delt med personen (`app.my_shares`, sak 232). Derfor
 * ingen `requires` — skjermen spør ingen celle, og porten er databasefunksjonene som bare svarer
 * for personen selv. Cella `export:create` gjelder som ellers, også i det personlige huset.
 *
 * `guest: false` på alle: i gjestemodus står fanen i organisasjonen som delte, og der hører
 * personens egne konserter og delinger fra andre ikke hjemme. Det delte selv eksporteres der det
 * ligger, med eksportene som har `guest: true` (advance, dagsark, lineup, plakat, vakter, ressurser).
 */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const col = (
  group: string,
  key: string,
  type: ExportColumnMeta['type'],
  extra: Omit<ExportColumnMeta, 'key' | 'labelKey' | 'type'> = {},
): ExportColumnMeta => ({
  key,
  labelKey: `export.artist.${group}.columns.${key}`,
  type,
  ...extra,
});

/**
 * MINE KONSERTER: lista på «Konserter» (`/concerts`), med visningen som er valgt der — kommende,
 * tidligere eller alle (`filter.view`). Kolonnene er tabellens, og stedets deler kan legges til.
 */
const CONCERTS: ExportMeta = {
  id: 'artist.concerts',
  domain: 'artist',
  kind: 'table',
  titleKey: 'export.artist.concerts.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  columns: [
    col('concerts', 'date', 'date'),
    col('concerts', 'time', 'time'),
    col('concerts', 'artist', 'text'),
    col('concerts', 'where', 'text'),
    col('concerts', 'status', 'status'),
    col('concerts', 'agency', 'text'),
    col('concerts', 'format', 'text', { defaultHidden: true }),
    col('concerts', 'endDate', 'date', { defaultHidden: true }),
    col('concerts', 'festival', 'text', { defaultHidden: true }),
    col('concerts', 'edition', 'text', { defaultHidden: true }),
    col('concerts', 'stage', 'text', { defaultHidden: true }),
    col('concerts', 'venue', 'text', { defaultHidden: true }),
    col('concerts', 'organiser', 'text', { defaultHidden: true }),
    col('concerts', 'city', 'text', { defaultHidden: true }),
    col('concerts', 'kind', 'status', { defaultHidden: true }),
  ],
  landscape: true,
};

/**
 * DAGSARKET FOR EN KONSERT: det artisten ser på konsertsiden (`/concerts/:kind/:id`), fane for
 * fane. Byrået bestemmer i Rettigheter hva artisten ser (0452–0453); en del er med bare når basen
 * har sendt den, som fanen. Hodet (dato, sted, agent og management) står alltid. Fra konserten
 * (`object`), og fra lista for alle i visningen (`filter`).
 */
const DAY_SHEET: ExportMeta = {
  id: 'artist.day-sheet',
  domain: 'artist',
  kind: 'document',
  titleKey: 'export.artist.daySheet.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'filter'],
  requires: [],
  guest: false,
  sections: [
    { key: 'schedule', labelKey: 'export.artist.daySheet.sections.schedule' },
    { key: 'contacts', labelKey: 'export.artist.daySheet.sections.contacts' },
    { key: 'people', labelKey: 'export.artist.daySheet.sections.people' },
    { key: 'travel', labelKey: 'export.artist.daySheet.sections.travel' },
    { key: 'stay', labelKey: 'export.artist.daySheet.sections.stay' },
    { key: 'technical', labelKey: 'export.artist.daySheet.sections.technical' },
    { key: 'hospitality', labelKey: 'export.artist.daySheet.sections.hospitality' },
    { key: 'promo', labelKey: 'export.artist.daySheet.sections.promo' },
    { key: 'other', labelKey: 'export.artist.daySheet.sections.other' },
    { key: 'deviations', labelKey: 'export.artist.daySheet.sections.deviations' },
    { key: 'documents', labelKey: 'export.artist.daySheet.sections.documents' },
    /* Bare når byrået har slått dem på (`/me/concerts/:kind/:id/deal`); standard er av. */
    { key: 'contract', labelKey: 'export.artist.daySheet.sections.contract' },
    { key: 'economy', labelKey: 'export.artist.daySheet.sections.economy' },
  ],
};

/**
 * DELT MED MEG: alt som er delt med personen, på tvers av organisasjonene, som én liste — det
 * delingskortene i Innboks viser (`GET /v1/me/shares`). Levende delinger, eller også de avsluttede
 * (`filter.status`).
 */
const SHARED_WITH_ME: ExportMeta = {
  id: 'artist.shared-with-me',
  domain: 'artist',
  kind: 'table',
  titleKey: 'export.artist.sharedWithMe.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  columns: [
    col('sharedWithMe', 'organization', 'text'),
    col('sharedWithMe', 'type', 'status'),
    col('sharedWithMe', 'title', 'text'),
    col('sharedWithMe', 'startsAt', 'datetime'),
    col('sharedWithMe', 'endsAt', 'datetime'),
    col('sharedWithMe', 'permission', 'status'),
    col('sharedWithMe', 'expiresAt', 'datetime'),
    col('sharedWithMe', 'status', 'status'),
    col('sharedWithMe', 'endedAt', 'datetime', { defaultHidden: true }),
    col('sharedWithMe', 'inCalendar', 'boolean', { defaultHidden: true }),
  ],
  landscape: true,
};

/**
 * DEN DELTE SALEN ELLER KVELDEN: lesedokumentet på `/delt/:grantId` (F18) — spesifikasjonen
 * (det samme dokumentet arrangøren og byrået leser i advancen), eller dato, tider og adresse.
 * Objektet er delingen (`object` = `grantId`); en avsluttet deling gir ingen fil.
 */
const SHARED_VENUE: ExportMeta = {
  id: 'artist.shared-venue',
  domain: 'artist',
  kind: 'document',
  titleKey: 'export.artist.sharedVenue.title',
  formats: ['pdf', 'docx'],
  scopes: ['object'],
  requires: [],
  guest: false,
  sections: [
    { key: 'night', labelKey: 'export.artist.sharedVenue.sections.night' },
    { key: 'spec', labelKey: 'export.artist.sharedVenue.sections.spec' },
    { key: 'layouts', labelKey: 'export.artist.sharedVenue.sections.layouts' },
    { key: 'fields', labelKey: 'export.artist.sharedVenue.sections.fields' },
    { key: 'files', labelKey: 'export.artist.sharedVenue.sections.files' },
  ],
};

export const ARTIST_EXPORTS: readonly ExportMeta[] = [
  CONCERTS,
  DAY_SHEET,
  SHARED_WITH_ME,
  SHARED_VENUE,
];
